import React from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LaunchIcon from "@mui/icons-material/Launch";
import DashboardIcon from "@mui/icons-material/Dashboard";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import SyncIcon from "@mui/icons-material/Sync";
import FlightIcon from "@mui/icons-material/Flight";

// Rebuilt as a live product page, matching AmyloidConnectPage's structure
// and tone — this previously described NephroTrax as an unreleased,
// aspirational "coming soon" waitlist product (dialysis center locator,
// travel booking, cost analysis — none of which exist), when it has
// actually shipped with a different, real feature set.
export default function NephroTraxPage() {
  const features = [
    {
      icon: <DashboardIcon sx={{ fontSize: 40 }} />,
      title: "Provider Dashboard & Attention Queue",
      description:
        "A real-time view of every patient who needs attention right now — surfaced automatically, not buried in a chart a provider has to go looking through.",
      color: "#00897b",
    },
    {
      icon: <NotificationsActiveIcon sx={{ fontSize: 40 }} />,
      title: "Automated Clinical Alerts",
      description:
        "Critical lab values, missed appointments, and concerning patient-reported symptoms generate an alert on their own — nothing depends on a provider remembering to check.",
      color: "#00695c",
    },
    {
      icon: <AssignmentTurnedInIcon sx={{ fontSize: 40 }} />,
      title: "Daily Patient Check-Ins",
      description:
        "A simple, mobile-first form for patients to log vitals, symptoms, and medication adherence between visits, so nothing concerning goes unnoticed until the next appointment.",
      color: "#00897b",
    },
    {
      icon: <SyncIcon sx={{ fontSize: 40 }} />,
      title: "Epic EHR Lab Sync",
      description:
        "Lab results sync automatically from Epic-connected health systems, with manual entry available for providers whose patients aren't yet connected.",
      color: "#00695c",
    },
    {
      icon: <FlightIcon sx={{ fontSize: 40 }} />,
      title: "Integrated Travel Booking",
      description:
        "Book flights, hotels, and ground transport for appointments directly from the platform — no separate service, no re-entering visit details.",
      color: "#00897b",
    },
  ];

  return (
    <Box>
      {/* HERO */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #00897b 0%, #00695c 100%)",
          color: "white",
          py: { xs: 8, md: 12 },
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={7}>
              <Chip
                label="Live Product"
                color="success"
                sx={{ fontWeight: 700, mb: 3 }}
              />
              <Typography variant="h2" fontWeight={800} gutterBottom>
                NephroTrax
              </Typography>
              <Typography
                variant="h5"
                sx={{ opacity: 0.9, mb: 4, fontWeight: 400 }}
              >
                A connected chronic kidney disease platform — daily check-ins
                for patients, a real-time attention queue for the providers
                caring for them.
              </Typography>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<LaunchIcon />}
                  onClick={() =>
                    window.open("https://www.nephrotrax.com", "_blank")
                  }
                  sx={{
                    bgcolor: "white",
                    color: "#00897b",
                    fontWeight: 700,
                    px: 4,
                    py: 1.5,
                    "&:hover": { bgcolor: "#f5f5f5" },
                  }}
                >
                  Launch App
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={5}>
              <Box
                sx={{
                  bgcolor: "rgba(255,255,255,0.15)",
                  borderRadius: 3,
                  p: 3,
                }}
              >
                {[
                  "Real-Time Provider Dashboard",
                  "Automated Clinical Alerts",
                  "Daily Patient Check-Ins",
                  "Epic EHR Integration",
                  "Integrated Travel Booking",
                  "Multi-Practice Provider Support",
                  "Free for Patients",
                ].map((item) => (
                  <Box
                    key={item}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      mb: 1.5,
                    }}
                  >
                    <CheckCircleIcon sx={{ color: "#a5f3d4" }} />
                    <Typography variant="body1">{item}</Typography>
                  </Box>
                ))}
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* FEATURES */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: "#f8fafc" }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", mb: 8 }}>
            <Typography variant="h3" fontWeight={700} gutterBottom>
              Platform Features
            </Typography>
          </Box>
          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Card
                  sx={{
                    height: "100%",
                    transition: "all 0.3s ease",
                    "&:hover": { transform: "translateY(-4px)", boxShadow: 4 },
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ color: feature.color, mb: 2 }}>
                      {feature.icon}
                    </Box>
                    <Typography variant="h6" fontWeight={700} gutterBottom>
                      {feature.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ lineHeight: 1.7 }}
                    >
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA */}
      <Box
        sx={{
          py: { xs: 8, md: 10 },
          background: "linear-gradient(135deg, #00897b 0%, #00695c 100%)",
          color: "white",
          textAlign: "center",
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h3" fontWeight={700} gutterBottom>
            Ready to Get Started?
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.9, mb: 4 }}>
            Join the patients and providers using NephroTrax to stay ahead of
            chronic kidney disease, between visits and beyond.
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<LaunchIcon />}
            onClick={() =>
              window.open("https://www.nephrotrax.com", "_blank")
            }
            sx={{
              bgcolor: "white",
              color: "#00897b",
              fontWeight: 700,
              px: 5,
              py: 1.5,
              "&:hover": { bgcolor: "#f5f5f5" },
            }}
          >
            Launch NephroTrax — It's Free
          </Button>
        </Container>
      </Box>
    </Box>
  );
}